import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import {
  DEFAULT_FAVORITE_FILTER,
  DEFAULT_HIDDEN_FILTER,
  type FavoriteFilter,
  type HiddenFilter,
} from '../logic/menuFilters'

interface MenuFiltersState {
  /**
   * Each menu's hidden filter (by area), for this visit only: a reload leaves
   * hidden rows out again, so showing them once never brings the clutter back.
   */
  hiddenFilters: Partial<Record<string, HiddenFilter>>
  /** Each menu's favorites filter (by area), kept across visits: a deliberate view. */
  favoriteFilters: Partial<Record<string, FavoriteFilter>>
  /**
   * Rows hidden while their menu was in use: still shown (dimmed) until the
   * pointer leaves the row, so a row never vanishes from under the pointer.
   */
  heldPaths: readonly string[]
  setHiddenFilter: (menu: string, filter: HiddenFilter) => void
  setFavoriteFilter: (menu: string, filter: FavoriteFilter) => void
  hold: (path: string) => void
  releaseHeld: () => void
  /** Lets every held row go but the one at `path` (the row under the pointer). */
  releaseHeldExcept: (path: string | null) => void
}

export const useMenuFiltersStore = create<MenuFiltersState>()(
  persist(
    (set) => ({
      hiddenFilters: {},
      favoriteFilters: {},
      heldPaths: [],
      setHiddenFilter: (menu, filter) =>
        set((state) => ({
          hiddenFilters: {
            ...state.hiddenFilters,
            [menu]: filter === DEFAULT_HIDDEN_FILTER ? undefined : filter,
          },
        })),
      setFavoriteFilter: (menu, filter) =>
        set((state) => ({
          favoriteFilters: {
            ...state.favoriteFilters,
            [menu]: filter === DEFAULT_FAVORITE_FILTER ? undefined : filter,
          },
        })),
      hold: (path) =>
        set((state) =>
          state.heldPaths.includes(path) ? state : { heldPaths: [...state.heldPaths, path] }
        ),
      releaseHeld: () => set((state) => (state.heldPaths.length ? { heldPaths: [] } : state)),
      releaseHeldExcept: (path) =>
        set((state) => {
          const kept = state.heldPaths.filter((held) => held === path)
          return kept.length === state.heldPaths.length ? state : { heldPaths: kept }
        }),
    }),
    {
      name: 'arvoke.sidebar.menuFilters',
      partialize: (state) => ({ favoriteFilters: state.favoriteFilters }),
    }
  )
)
